import React from 'react';
import Customer from '../code/models/Customer';
import { stringToDate } from '../code/helpers/stringToDate';
import '../css/customerdatatable.css';
import parsePhoneNumber from 'libphonenumber-js';
import Api_Config from "../code/api/ApiConfig";

interface ICustomerDataTable {
  tableData: Customer[] | null,
  currentCustomerId: number,
  customerSelectedCallback(customerData: Customer): void,
  customerActionCallback(customerData: Customer): void
}

class TableSortInfo {
  column: string = "";
  direction: string = "asc";
}

export default function CustomerDataTable({ tableData, currentCustomerId, customerSelectedCallback, customerActionCallback }: ICustomerDataTable) {

  const [selectedCustomerId, setSelectedCustomerId] = React.useState<number>(currentCustomerId);
  const [selectedSort, setSelectedSort] = React.useState<TableSortInfo>(() => { return new TableSortInfo(); });
  const company = Api_Config.getSelectedCompany();

  function handleOnClick(rowData: any) {
    setSelectedCustomerId(rowData.customerId);
    customerSelectedCallback(rowData);
  }

  function handleOnDoubleClick(rowData: any) {
    setSelectedCustomerId(rowData.customerId);
    customerActionCallback(rowData);
  }

  function handleOnHeaderColumnClick(column: string) {
    var newSort: TableSortInfo = {
      column,
      direction: "asc"
    };

    if (selectedSort.column === newSort.column) {
      newSort.direction = selectedSort.direction === "asc" ? "desc" : "asc";
    }

    setSelectedSort(newSort);
  }

  function getFormattedPhone(rowData: Customer): string {
    if (!rowData.formattedPhone) {
      const phnnmbr = `+${rowData.phoneCountryCode || "1"}${rowData.phone}`;
      const phoneNumber = parsePhoneNumber(phnnmbr);
      if (phoneNumber?.isPossible()) {
        rowData.formattedPhone = phoneNumber?.formatInternational() || phnnmbr;
      } else {
        rowData.formattedPhone = phnnmbr;
      }
    }
    return rowData.formattedPhone;
  }

  function buildRow(rowData: Customer, index: number) {
    var selectedClass = (rowData.customerId === selectedCustomerId) ? " selected" : "";

    let rowClass = `${index % 2 === 0 ? "datarow shaded" : "datarow"} ${selectedClass}`;

    return (
      <tr key={rowData.customerId} className={rowClass} onClick={() => handleOnClick(rowData)} onDoubleClick={() => handleOnDoubleClick(rowData)}>
        <td>{rowData.userId}</td>
        <td>{rowData.companyEmployeeId || rowData.companyId}</td>
        <td>{rowData.name}</td>
        <td>{getFormattedPhone(rowData)}</td>
        <td>{rowData.email}</td>
        <td>{stringToDate(rowData.created)}</td>
        <td className={"checkbox"}><input type="checkbox" readOnly checked={rowData.skipBidding} /></td>
        <td className={"checkbox"}><input type="checkbox" readOnly checked={rowData.deactivated} /></td>
      </tr>)
  }

  function getTableHeaderClassNames(column: string): string {
    var className = "table-header-col sortable";

    if (selectedSort.column === column) {
      className += selectedSort.direction === "asc" ? " asc" : " desc";
    }

    return className;
  }

  function BuildRows(): React.ReactNode {
    if (!tableData) {
      return;
    }

    var customerRows = tableData;

    if (!!selectedSort.column) {
      switch (selectedSort.column) {
        case "uid":
          customerRows = tableData.sort((a, b) => a.userId > b.userId ? 1 : -1);
          break;
        case "unum":
          customerRows = tableData.sort((a, b) => (a.companyEmployeeId || a.companyId) > (b.companyEmployeeId || b.companyId) ? 1 : -1);
          break;
        case "name":
          customerRows = tableData.sort((a, b) => a.name > b.name ? 1 : -1);
          break;
        case "phone":
          customerRows = tableData.sort((a, b) => a.phone > b.phone ? 1 : -1);
          break;
        case "email":
          customerRows = tableData.sort((a, b) => a.email > b.email ? 1 : -1);
          break;
        case "created":
          customerRows = tableData.sort((a, b) => a.created > b.created ? 1 : -1);
          break;
      }
    }

    if (selectedSort.direction !== "asc") {
      customerRows = customerRows.reverse();
    }

    return customerRows.map((rowData: any, index: number) => {
      return buildRow(rowData, index);
    });
  }

  return (
    <div className="customer-table-wrapper">
      <table className="customer-table">
        <thead>
          <tr className="table-header-row">
            <td className={getTableHeaderClassNames("uid")} onClick={() => handleOnHeaderColumnClick("uid")}>User Id</td>
            <td className={getTableHeaderClassNames("unum")} onClick={() => handleOnHeaderColumnClick("unum")}>Employee Id</td>
            <td className={getTableHeaderClassNames("name")} onClick={() => handleOnHeaderColumnClick("name")}>Name</td>
            <td className={getTableHeaderClassNames("phone")} onClick={() => handleOnHeaderColumnClick("phone")}>Phone</td>
            <td className={getTableHeaderClassNames("email")} onClick={() => handleOnHeaderColumnClick("email")}>Email</td>
            <td className={getTableHeaderClassNames("created")} onClick={() => handleOnHeaderColumnClick("created")}>Created</td>
            <td className="table-header-col checkbox">Skip</td>
            <td className="table-header-col checkbox">Deactivated</td>
          </tr>
        </thead>
        <tbody className="table-body">
          {BuildRows()}
        </tbody>
      </table>
    </div>
  )
}